import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popper,
  PopperPlacementType,
  styled,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { ColDef } from 'ag-grid-community'
import { intl } from '../../../../../i18n'
import { Button } from '../../../../components/buttons'
import { FontSize } from '../../../../../styles/commonStyles'
import { colorPalette } from '../../../../style/colorPalette'
import { useCallback } from 'react'

type Props = {
  open: boolean
  anchorEl: HTMLButtonElement | null
  filteredColumns?: ColDef[]
  placement?: PopperPlacementType
  onDeleteFilteredColumn?: (column: ColDef) => void
  resetFilters: () => void
  getDisplayColumnName?: (colDef: ColDef) => string | undefined
}

const StyledPopper = styled(Popper)(() => ({
  zIndex: 1300,
  background: colorPalette.monotone[0],
  borderRadius: '4px',
  boxShadow: '0px 1px 2px #0000004D',
}))

const StyledList = styled(List)(() => ({
  position: 'relative',
  overflow: 'auto',
  width: 'fit-content',
  padding: '10px',
  maxHeight: '430px',
}))

const StyledFilterCriteria = styled('span')(() => ({
  fontSize: FontSize.MEDIUM,
  fontWeight: 400,
  textAlign: 'center',
  display: 'block',
}))

const StyledButton = styled(Button)(() => ({
  border: 'none',
  background: 'transparent',
  padding: '15px 15px 15px 15px',
  cursor: 'pointer',
  width: '100%',
}))

const StyledPopperReset = styled('span')(() => ({
  color: colorPalette.pink[6],
  fontSize: FontSize.MEDIUM,
  fontWeight: 400,
}))

const StyledListItem = styled(ListItem)(() => ({
  padding: '0px',
  background: colorPalette.monotone[1],
  paddingRight: '8px',
  paddingLeft: '8px',
  border: 'none',
  borderRadius: '32px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '5px 0 10px 0',
}))

const StyledListItemText = styled(ListItemText)(() => ({
  width: 'fit-content',
  minWidth: '150px',
  height: '30px',
  border: 'none',
  color: colorPalette.monotone[4],
  fontSize: FontSize.MEDIUM,
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 12px 0 16px',
  textAlign: 'center',
  margin: 0,
}))

const StyledIconButton = styled(IconButton)(() => ({
  padding: '3px',
  background: colorPalette.monotone[0],
}))

const StyledCloseIcon = styled(CloseIcon)(() => ({
  height: '10px',
  width: '10px',
}))

const FilterPopper = ({
  open,
  anchorEl,
  filteredColumns,
  placement,
  onDeleteFilteredColumn,
  resetFilters,
  getDisplayColumnName,
}: Props) => {
  const getDisplayValue = useCallback(
    (colDef: ColDef): string => {
      const value = getDisplayColumnName
        ? getDisplayColumnName(colDef)
        : undefined
      return value ?? colDef.headerName ?? ''
    },
    [getDisplayColumnName]
  )
  return (
    <StyledPopper
      open={open}
      anchorEl={anchorEl}
      placement={placement || 'right-start'}
      disablePortal={true}
    >
      <StyledList as="nav">
        <StyledFilterCriteria>
          {intl.formatMessage({ id: 'wbs.header.filter.criteria' })}
        </StyledFilterCriteria>
        <StyledButton colorPattern="monotone" onClick={resetFilters}>
          <StyledPopperReset>
            {intl.formatMessage({ id: 'wbs.header.filter.popper.reset' })}
          </StyledPopperReset>
        </StyledButton>
        {filteredColumns?.map((column: ColDef, index) => {
          return (
            <StyledListItem
              key={`filteredColumn${index}`}
              onClick={e => {
                e.stopPropagation()
              }}
            >
              <StyledListItemText primary={getDisplayValue(column)} />
              {onDeleteFilteredColumn && (
                <StyledIconButton
                  onClick={() => onDeleteFilteredColumn(column)}
                >
                  <StyledCloseIcon fontSize="small" />
                </StyledIconButton>
              )}
            </StyledListItem>
          )
        })}
      </StyledList>
    </StyledPopper>
  )
}

export default FilterPopper
